/**
 * 1.6.5 "Review visit" (D-1.6.5-6) — one screen for what happened to a visit: the visit, the timeline (the
 * scheduler's answers, the crew's stamps, the app's own audit), the close-out as recorded, the photos and files, and
 * the office actions that apply. `GET /api/visits/{appointmentId}/review` answers it; the page is
 * `/visits/:appointmentId`.
 *
 * Who sees what — 1.6.7 (D-1.6.7-0, Elijah's rule of 7 Oct; it replaces 1.6.5's first rule):
 *   administrator, co-administrator   everything, on any visit
 *   scheduler                         any visit's timeline; the close-out's words, its report and its files only for a
 *                                     close-out they wrote — for the others, the fact that one was recorded
 *   crew                              read-only, their own crew's visits (today's — on a two-crew day their lane's —
 *                                     and an earlier one they arrived at or closed out): the timeline, the close-out
 *                                     they wrote, their photos; no action buttons at all
 *   viewer                            the visit, the timeline and the fact of a close-out (as in 1.6.5)
 *   tech                              never
 * The server decides (api/src/reads/review-access.ts) and leaves the rest out of the answer.
 *
 * The buttons on the screen: Edit close-out and Send update are the administrator's and the co-administrator's;
 * Reopen and Move are the administrator's — a co-administrator sees those two greyed, "Administrator only".
 */
import type { AppointmentRow } from './api-types';
import type { ArrivalAnswer, DoorNext, DoorReason } from './arrival';
import type { CloseoutForm } from './closeout';
import type { Bilingual, CloseoutMarker } from './finish';
import type { Role } from './roles';
import type { Media, PhotoWhen } from './visit-files';
import { staffName } from './people';

/** Who may open the review: History's roles and, from 1.6.7, the crew (their own crew's visits — the server's rule). */
export const REVIEW_ROLES: readonly Role[] = ['admin', 'coadmin', 'scheduler', 'crew', 'viewer'];
/** Who sees the close-out's words, the report and the photos of ANY visit (the photos' own rule, PHOTO_VIEWERS). */
export const REVIEW_DETAIL_ROLES: readonly Role[] = ['admin', 'coadmin'];
/** 1.6.7: who sees them for a close-out they wrote themselves, and for no other. */
export const REVIEW_OWN_ROLES: readonly Role[] = ['scheduler', 'crew'];
/** 1.6.7: on Today's card the button sits in the row of buttons for these; for the others it stays beside the name. */
export const REVIEW_ROW_ROLES: readonly Role[] = ['admin', 'coadmin', 'scheduler'];

/** 1.6.7: the screen's buttons. `adminOnly`: shown greyed with the words "Administrator only". */
export type ReviewAction = 'edit' | 'sendUpdate' | 'reopen' | 'move';
export type ReviewActionState = 'yes' | 'adminOnly' | 'no';
export function reviewActionFor(action: ReviewAction, role: Role | null | undefined): ReviewActionState {
  if (role === 'admin') return 'yes';
  if (role !== 'coadmin') return 'no';
  return action === 'edit' || action === 'sendUpdate' ? 'yes' : 'adminOnly';
}

export const canReviewVisit = (role: Role | null | undefined): boolean => !!role && REVIEW_ROLES.includes(role);
export const seesReviewDetail = (role: Role | null | undefined): boolean => !!role && REVIEW_DETAIL_ROLES.includes(role);

/** The page's address for a visit. */
export const reviewPath = (appointmentId: string): string => `/visits/${encodeURIComponent(appointmentId)}`;

/** "brigida.lopez@abfselis.org" → "Brigida" — the name a timeline line carries. */
export function firstNameOf(email: string | null | undefined): string | null {
  const e = (email ?? '').trim();
  if (!e) return null;
  return staffName(e).split(' ')[0] || null;
}

export const REVIEW_EVENT_KINDS = [
  'booked',
  'movedHere',
  'movedAway',
  'cancelled',
  'answer',
  'hold',
  'holdExtended',
  'holdReleased',
  'started',
  'noShow',
  'unable',
  'reopened',
  'arrived',
  'arrivalUndone',
  'closeout',
  'closeoutEdited',
  'closeoutReopened',
  'updateSent',
  'markedCompleted',
  'markCompletedUndone',
  'returned',
  'voided',
  'reviewed',
  'fileAdded',
  'addendum',
  'other',
] as const;
export type ReviewEventKind = (typeof REVIEW_EVENT_KINDS)[number];

/** One line of the timeline. */
export interface ReviewEvent {
  /** The instant (ISO). */
  at: string;
  kind: ReviewEventKind;
  /** The person's first name; null when no person pressed (the monthly plan, an import). */
  by: string | null;
  /** The tenant's own press on their link — said as "the tenant", never by an address. */
  byTenant?: boolean;
  /** `answer`: Confirmed | Declined | Unable To Reach (the stored words). */
  answer?: string;
  /** `arrived`: what the crew found at the door. */
  door?: ArrivalAnswer;
  doorReason?: DoorReason | null;
  doorNext?: DoorNext | null;
  /** `arrived`: the presser's crew, when the stamp carries one. */
  crew?: 'A' | 'B' | null;
  /** `arrivalUndone` / `markCompletedUndone`: cleared by the office after the window. */
  office?: boolean;
  /** `arrivalUndone` / `markCompletedUndone`: the reason's stored words ("pressed by mistake"). */
  reason?: string;
  /** `closeoutEdited` / `updateSent`: the report version. */
  version?: number;
  /** `movedHere` / `movedAway`: the other visit of the move. */
  otherVisit?: string | null;
  /** `cancelled`: who asked (the stored words). */
  initiatedBy?: string | null;
  /** `fileAdded`: the file's name — only for those who see the files. */
  fileName?: string;
  /** `addendum` (1.6.8): which addendum to the report (1, 2, …). */
  addendum?: number;
  /** `other`: the event's own code, for a line nobody has words for yet. */
  code?: string;
}

/** A report version or a photo: where it is and the app's own address that opens it. */
export interface ReviewFile {
  name: string;
  path: string;
  size: number;
  media: Media;
  /** The existing files API address that opens it full size. */
  href: string;
  /** 1.6.7: the visit's `Before/` or `After/` folder; null for a file in the folder itself (a report, an older photo). */
  when: PhotoWhen | null;
  /** Added through "Add a file": who (first name) and when. */
  addedBy?: string | null;
  addedAt?: string | null;
  mistake?: boolean;
}

export interface ReviewFolder {
  which: 'history' | 'incident' | 'unitCondition';
  folder: string;
  /** The folder's own page in SharePoint, when the site gives one. */
  webUrl: string | null;
  /** 1.6.7: false for a folder from before Before / After — it has photos and neither subfolder ("not sorted"). */
  sorted: boolean;
  files: ReviewFile[];
}

/** A free text as written, with the English the app keeps beside it (null when it is English, or not translated). */
export interface ReviewWords {
  original: string;
  english: string | null;
}

/** The close-out as recorded — an administrator's or a co-administrator's, or (1.6.7) the person's who wrote it. */
export interface ReviewCloseoutDetail {
  form: CloseoutForm;
  /** The questions' own words, for the rooms and the standard-clean answers. */
  rooms: { key: string; label: Bilingual; done: boolean }[];
  standard: { key: string; label: Bilingual; yes: boolean }[];
  /** English beside each free text, by the form's field (notes, incident, observationSummary, …). */
  words: Record<string, ReviewWords>;
  /** The report versions, oldest first; the last is the current one. 1.6.7: only those whose file is in the folder. */
  reports: { version: number; at: string; by: string | null; href: string }[];
  memos: { code: string; at: string; status: string }[];
}

export interface ReviewCloseout {
  /** Who closed it and when, the report version, whether an edit waits to be sent (what Today's card already says). */
  marker: CloseoutMarker;
  by: string | null;
  /** Absent for a role that sees only the fact. */
  detail?: ReviewCloseoutDetail;
}

export interface VisitReview {
  appointment: AppointmentRow;
  /** The planned length in minutes (the booking's How long, else the setting for its kind). */
  lengthMinutes: number;
  /** Staff units and facility spaces are marked completed; a tenant's visit takes a close-out. */
  completionRequired: boolean;
  /** Oldest first, newest last. */
  timeline: ReviewEvent[];
  /** Null when no close-out was recorded through the app. */
  closeout: ReviewCloseout | null;
  /**
   * Absent for someone who sees no files; otherwise the visit's folders that hold something. 1.6.7: a scheduler or a
   * crew member who pressed the visit's Arrived and did not write its close-out gets its Before / After photos only.
   */
  folders?: ReviewFolder[];
  /** What this answer holds: the close-out's words and the files, or only the facts. */
  detail: boolean;
  /** 1.6.8: what was added to the report afterwards, oldest first — an administrator's and a co-administrator's only (absent for anyone else). */
  addenda?: ReviewAddendum[];
}

/* ----------------------------------------------------------------------------------------------
 * 1.6.8 (D-1.6.8-5) — an addendum to a close-out report: something found out afterwards that the visit's close-out
 * did not report. It adds on (`report-addendum-<n>.pdf` beside the report); the report itself is never rewritten.
 * POST /api/visits/{appointmentId}/addendum — an administrator or a co-administrator.
 * -------------------------------------------------------------------------------------------- */

/** Who adds to a report, and who sees what was added. */
export const ADDENDUM_ROLES: readonly Role[] = ['admin', 'coadmin'];
export const canAddAddendum = (role: Role | null | undefined): boolean => !!role && ADDENDUM_ROLES.includes(role);

export const ADDENDUM_WHAT_MAX = 4000;
export const ADDENDUM_WHO_MAX = 120;

/** The file's name beside the report: report-addendum-1.pdf, report-addendum-2.pdf … */
export const addendumFileName = (n: number): string => `report-addendum-${n}.pdf`;
export const ADDENDUM_FILE = /^report-addendum-(\d+)\.pdf$/i;

/**
 * Where "Add to the report" is offered: a visit that takes a close-out (a tenant's — it has a folder for the report)
 * and has one recorded through the app; and, with no close-out on record (a no-show, a visit closed before the
 * app), once its day is over — something that was not reported can be found out about those too. Never a visit
 * still to come, a staff unit or a facility space.
 */
export function addendumOffered(v: { completionRequired: boolean; dayKey: string; hasCloseout: boolean }, todayKey: string): boolean {
  if (!v.completionRequired || !/^\d{8}$/.test(v.dayKey)) return false;
  return v.hasCloseout ? v.dayKey <= todayKey : v.dayKey < todayKey;
}

/** The body of POST /api/visits/{appointmentId}/addendum. */
export interface AddendumBody {
  /** One per opening of the sheet: the same press sent twice writes one addendum. */
  key: string;
  /** What was found — kept exactly as written. */
  what: string;
  /** The day it refers to (yyyy-mm-dd) — never in the future. */
  refersTo: string;
  /** Who told the office ("the tenant's daughter") — not necessarily someone with an account. */
  reportedBy: string;
  incident: boolean;
  unitCondition: boolean;
}

export interface ReviewAddendum {
  n: number;
  /** When it was added (ISO) and who added it (first name). */
  at: string;
  by: string | null;
  /** yyyy-mm-dd */
  refersTo: string;
  reportedBy: string;
  /** As written, with the English the app keeps beside it. */
  words: ReviewWords;
  incident: boolean;
  unitCondition: boolean;
  /** The app's own address that opens `report-addendum-<n>.pdf`. */
  href: string;
  memos: { code: string; at: string; status: string }[];
}

export interface AddendumResponse {
  addendum: ReviewAddendum;
  /** The same press arrived twice: this is the addendum the first one wrote. */
  duplicate: boolean;
}
